<div class="close-cross">
  <button mat-icon-button color="warn" (click)="close()">
    <mat-icon>close</mat-icon>
  </button>
</div>

<!-- ── STEP 1 : CAPTCHA ───────────────────────────────────────────── -->
<ng-container *ngIf="step === 'captcha'">
  <mat-dialog-content>
    <h2>{{ "page.login.forgotPassword.title" | translate }}</h2>

    <!-- Success: email sent -->
    <ng-container *ngIf="captchaSent">
      <p class="success-message">{{ "page.login.forgotPassword.emailSent" | translate }}</p>
      <div class="action-row">
        <button mat-raised-button color="primary" (click)="goToResetStep()">
          {{ "page.login.forgotPassword.enterResetCode" | translate }}
        </button>
      </div>
    </ng-container>

    <!-- Captcha form -->
    <ng-container *ngIf="!captchaSent">
      <form [formGroup]="captchaForm" autocomplete="off">
        <!-- Login field -->
        <mat-form-field>
          <mat-label>{{ "page.login.email" | translate }}</mat-label>
          <input matInput formControlName="login" [placeholder]="'page.login.email' | translate" />
          <mat-error *ngIf="isFieldValid('login')">{{ "page.login.email-required" | translate }}</mat-error>
        </mat-form-field>

        <!-- Captcha image + countdown -->
        <div class="captcha-container" *ngIf="captchaLoaded || captchaLoading">
          <div class="captcha-image-wrapper">
            <img *ngIf="captchaLoaded && !captchaLoading" [src]="captchaImageUrl" alt="captcha" class="captcha-image" />
            <div *ngIf="captchaLoading" class="captcha-loader">
              <mat-progress-spinner mode="indeterminate" diameter="48"></mat-progress-spinner>
            </div>
          </div>
          <div class="countdown-wrapper" *ngIf="captchaLoaded && !captchaLoading">
            <div class="countdown-ring">
              <span class="countdown-value">{{ countdown }}</span>
            </div>
            <button mat-icon-button type="button" (click)="refreshCaptchaManually()" [disabled]="captchaBlocked" class="refresh-btn">
              <mat-icon>refresh</mat-icon>
            </button>
          </div>
        </div>

        <!-- Captcha code field (visible only once captcha loaded) -->
        <mat-form-field *ngIf="captchaLoaded">
          <mat-label>{{ "page.login.forgotPassword.captchaCode" | translate }}</mat-label>
          <input matInput formControlName="captchaCode" autocomplete="off" />
          <mat-error *ngIf="captchaForm.get('captchaCode').touched && captchaForm.get('captchaCode').hasError('required')">
            {{ "page.login.forgotPassword.captchaCodeRequired" | translate }}
          </mat-error>
        </mat-form-field>

        <!-- Blocked message -->
        <p *ngIf="captchaBlocked" class="error">{{ "page.login.captchaMaxAttempts" | translate }}</p>

        <!-- Server error -->
        <p *ngIf="captchaError && !captchaBlocked" class="error">{{ captchaError }}</p>
      </form>
    </ng-container>
  </mat-dialog-content>

  <mat-dialog-actions class="btn-container" *ngIf="!captchaSent">
    <button mat-raised-button (click)="close()">{{ "btn.cancel" | translate }}</button>
    <button mat-raised-button color="primary" (click)="submitCaptcha()" [disabled]="captchaLoading || captchaBlocked">
      {{ captchaLoaded ? ("btn.send" | translate) : ("page.login.forgotPassword.requestReset" | translate) }}
    </button>
  </mat-dialog-actions>

  <!-- Already have a reset code -->
  <div class="already-have-code" *ngIf="!captchaSent">
    <button mat-button (click)="goToResetStep()">
      {{ "page.login.forgotPassword.alreadyHaveCode" | translate }}
    </button>
  </div>
</ng-container>

<!-- ── STEP 2 : RESET PASSWORD ────────────────────────────────────── -->
<ng-container *ngIf="step === 'reset'">
  <mat-dialog-content>
    <h2>{{ "page.login.resetPasswordTitle" | translate }}</h2>

    <form [formGroup]="resetForm" autocomplete="off">
      <!-- Hidden autocomplete workaround -->
      <div style="display: none"><input type="password" /></div>

      <!-- Login -->
      <mat-form-field>
        <mat-label>{{ "page.login.email" | translate }}</mat-label>
        <input matInput formControlName="login" autocomplete="off" />
        <mat-error *ngIf="resetForm.get('login').touched && resetForm.get('login').hasError('required')">
          {{ "page.login.email-required" | translate }}
        </mat-error>
      </mat-form-field>

      <!-- Reset code -->
      <mat-form-field>
        <mat-label>{{ "page.login.forgotPassword.resetCode" | translate }}</mat-label>
        <input matInput formControlName="resetCode" autocomplete="off" />
        <mat-error *ngIf="resetForm.get('resetCode').touched && resetForm.get('resetCode').hasError('required')">
          {{ "page.login.forgotPassword.resetCodeRequired" | translate }}
        </mat-error>
      </mat-form-field>

      <!-- New password -->
      <mat-form-field [ngStyle]="resetForm.get('newPasswordA').touched && resetForm.get('newPasswordA').invalid ? { height: '5rem' } : ''">
        <mat-label>{{ "page.login.newPassword" | translate }}</mat-label>
        <app-show-hide-password>
          <input matInput autocomplete="off" type="password" formControlName="newPasswordA" />
        </app-show-hide-password>
        <mat-error *ngIf="resetForm.get('newPasswordA').touched && resetForm.get('newPasswordA').hasError('required')">
          {{ "page.login.password-required" | translate }}
        </mat-error>
        <mat-error *ngIf="resetForm.get('newPasswordA').touched && resetForm.get('newPasswordA').hasError('pattern')">
          {{ "page.login.password-notValid" | translate }}
        </mat-error>
      </mat-form-field>

      <!-- Confirm password -->
      <mat-form-field>
        <mat-label>{{ "page.login.confirmNewPassword" | translate }}</mat-label>
        <app-show-hide-password>
          <input matInput autocomplete="off" type="password" formControlName="newPasswordB" />
        </app-show-hide-password>
        <mat-error *ngIf="resetForm.get('newPasswordB').touched && resetForm.get('newPasswordB').hasError('matching')">
          {{ "page.login.password-not-same" | translate }}
        </mat-error>
      </mat-form-field>

      <!-- Server error -->
      <mat-error *ngIf="resetError">{{ "api.errors." + resetError | translate }}</mat-error>
    </form>
  </mat-dialog-content>

  <mat-dialog-actions class="btn-container">
    <button mat-raised-button (click)="close()" [disabled]="resetLoading">{{ "btn.cancel" | translate }}</button>
    <button mat-raised-button color="primary" (click)="submitResetPassword()" [disabled]="resetLoading">
      <mat-spinner *ngIf="resetLoading" diameter="20" class="inline-spinner"></mat-spinner>
      {{ "btn.reset" | translate }}
    </button>
  </mat-dialog-actions>
</ng-container>
